import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer} from "react";
import {arrayMove} from "@dnd-kit/sortable";

import * as api from '../../../services/api';

import {useFeedbackTools} from "../../../components/FeedbackTools";
import {apartmentMapper, homeMapper, homePhotoMapper, homeServiceJoinMapper} from '../../../mapper/homeMapper'
import lepri from "../../../services/lepri-js";
import {useAccommodationsPage} from "../AccommodationContext";


const HomeContext = createContext();
export const useHome = () => useContext(HomeContext);

const defaultHomeState = {
    id: null,
    name: "",
    addressStreet: "",
    addressNumber: "",
    mobilePhone: "",
    phone: "",
    cirCode: "",
    cinCode: "",
    icon: "",
    descriptionIt: "",
    descriptionEn: "",
    position: {latitude: 0, longitude: 0},
    category: 3,
    arrivalDate: 6,
    apartments: [],
    serviceJoins: [],
    photos: [],
    propertyInstructions: [],
}

export default function HomeProvider({children}) {
    const {selectedHomeId} = useAccommodationsPage();
    const {showErrorNotification} = useFeedbackTools;

    const [homeState, homeDispatcher] = useReducer(
        (state, action) => {
            switch (action.type) {
                case 'setAttributes':
                    return {...state, ...action.attributes}
                case 'createPhoto':
                    return {...state, photos: [...state.photos, action.photo]}
                case 'movePhoto':
                    return {...state, photos: arrayMove(state.photos, action.oldIndex, action.newIndex)}
                case 'deletePhoto':
                    return {...state, photos: state.photos.filter((p) => p.id !== action.photoId)}
                case 'updateApartment':
                    return {...state, apartments: state.apartments.map(a => a.id === action.apartment.id ? action.apartment : a)}
                case 'createApartment':
                    return {...state, apartments: [...state.apartments, action.apartment]}
                case 'deleteApartment':
                    return {...state, apartments: state.apartments.filter((a) => a.id !== action.apartmentId)}
                default:
                    throw new Error(`Cannot find action ${action.type} for homeState reducer`);
            }
        },
        {
            isLoading: true,
            ...defaultHomeState,
        }
    )

    const loadData = useCallback(async () => {
        if (selectedHomeId) {
            try {
                homeDispatcher({type: "setAttributes", attributes: {isLoading: true}});
                const data = await api.Homes.getHome(selectedHomeId);
                homeDispatcher({type: "setAttributes", attributes: {isLoading: false, ...homeMapper(data)}})
            } catch (e) {
                showErrorNotification(e);
            }
        }
        homeDispatcher({type: "setAttributes", attributes: {isLoading: false}})
    }, [selectedHomeId])  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(
        () => {
            loadData();
        },
        [loadData] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const deletePhoto = useCallback(
        async (photoId) => {
            await api.Homes.deleteHomePhoto(photoId)
            homeDispatcher({type: "deletePhoto", photoId: photoId})
        }, []
    )

    const movePhoto = useCallback(
        async (photoId, oldIndex, newIndex) => {
            // first move the photo in the state to get an immediate visual response
            // then call the api to update the position
            homeDispatcher({type: "movePhoto", oldIndex: oldIndex, newIndex: newIndex});
            await api.Homes.updateHomePhoto(photoId, {orderPosition: newIndex});
        },
        []
    )

    const appendPhoto = useCallback(
        async (file) => {
            const image = await lepri(file);
            const formData = new FormData();

            const maxOrderPosition = homeState.photos.reduce(
                (acc, val) => val.orderPosition > acc ? val.orderPosition : acc,
                0
            );

            formData.append("order_position", maxOrderPosition + 1);
            formData.append("title_it", homeState.name);
            formData.append("title_en", homeState.name);
            formData.append("photo", image);
            const res = await api.Homes.createHomePhoto(selectedHomeId, formData)
            const photo = homePhotoMapper(res);
            homeDispatcher({type: "createPhoto", photo: photo});
        },
        [selectedHomeId, homeState.photos, homeState.name]
    )

    const updateHome = useCallback(
        async (data) => {
            const homeData = await api.Homes.updateHome(selectedHomeId, data);
            homeDispatcher({type: "setAttributes", attributes: homeMapper(homeData)})
        }, [selectedHomeId]
    )

    const updateIcon = useCallback(
        async (file) => {
            const image = await lepri(file);
            const formData = new FormData();
            formData.append("icon", image);
            const homeData = await api.Homes.updateHomeIcon(selectedHomeId, formData);
            homeDispatcher({type: "setAttributes", attributes: homeMapper(homeData)})
        },
        [selectedHomeId]
    )

    const updateHomeServiceJoins = useCallback(
        async (data) => {
            const serviceJoins = await api.Homes.updateHomeServiceJoins(selectedHomeId, data);
            homeDispatcher({type: "setAttributes", attributes: {serviceJoins: serviceJoins.map(homeServiceJoinMapper)}})
        }, [selectedHomeId]
    )

    const createApartment = useCallback(
        async (data) => {
            const apartmentData = await api.Apartments.createApartment(selectedHomeId, data);
            const apartment = apartmentMapper(apartmentData);
            homeDispatcher({type: "createApartment", apartment: apartment});
            return apartment;
        }, [selectedHomeId]
    )

    const refreshApartment = useCallback(
        (data) => homeDispatcher({type: "updateApartment", apartment: apartmentMapper(data)}),
        [homeDispatcher]
    )

    const deleteApartment = useCallback(
        (apartmentId) => homeDispatcher({type: "deleteApartment", apartmentId: apartmentId}),
        [homeDispatcher]
    )

    const infoScore = useMemo(
        () => {
            const checkAttr = (value) => {
                return !!value && value !== "" && (!Array.isArray(value) || value.length > 0)
            }

            let missingAttr = []
            let okAttr = []

            checkAttr(homeState.name) ? okAttr.push("Nome") : missingAttr.push("Nome");
            checkAttr(homeState.mail) ? okAttr.push("Mail") : missingAttr.push("Mail");
            checkAttr(homeState.addressStreet) && checkAttr(homeState.addressNumber) ?
                okAttr.push("Indirizzo") : missingAttr.push("Indirizzo");
            checkAttr(homeState.position?.latitude) && checkAttr(homeState.position?.longitude) ?
                okAttr.push("Posizione sulla mappa") : missingAttr.push("Posizione sulla mappa");
            checkAttr(homeState.mobilePhone) || checkAttr(homeState.phone) ?
                okAttr.push("Recapito telefonico") : missingAttr.push("Recapito telefonico");
            checkAttr(homeState.cirCode) ? okAttr.push("Codice CIR") : missingAttr.push("Codice CIR");
            !homeState.cinCodeMissing || checkAttr(homeState.cinCode) ? okAttr.push("Codice CIN") : missingAttr.push("Codice CIN Struttura/Apparatemnti");
            checkAttr(homeState.icon) ? okAttr.push("Icona") : missingAttr.push("Icona");
            checkAttr(homeState.category) ? okAttr.push("Categoria") : missingAttr.push("Categoria");
            checkAttr(homeState.descriptionIt) ? okAttr.push("Descrizione (italiano)") : missingAttr.push("Descrizione (italiano)");
            checkAttr(homeState.descriptionEn) ? okAttr.push("Descrizione (inglese)") : missingAttr.push("Descrizione (inglese)");
            checkAttr(homeState.arrivalDay) ? okAttr.push("Giorno di arrivo") : missingAttr.push("Giorno di arrivo");
            checkAttr(homeState.checkIn) ? okAttr.push("Orario check-in predefinito") : missingAttr.push("Orario check-in predefinito");
            checkAttr(homeState.checkOut) ? okAttr.push("Orario check-out predefinito") : missingAttr.push("Orario check-out predefinito");
            checkAttr(homeState.photos) ? okAttr.push("Foto esterne") : missingAttr.push("Foto esterne");
            checkAttr(homeState.serviceJoins) ? okAttr.push("Servizi") : missingAttr.push("Servizi");
            checkAttr(homeState.apartments) ? okAttr.push("Appartamenti") : missingAttr.push("Appartamenti");

            return {
                score: `${okAttr.length}/${okAttr.length + missingAttr.length}`,
                missingAttribute: missingAttr
            }
        },
        [homeState]
    )

    const context = {
        isLoading: homeState.isLoading,
        id: homeState.id,
        name: homeState.name,
        mail: homeState.mail,
        addressStreet: homeState.addressStreet,
        addressNumber: homeState.addressNumber,
        mobilePhone: homeState.mobilePhone,
        phone: homeState.phone,
        cirCode: homeState.cirCode,
        cinCode: homeState.cinCode,
        icon: homeState.icon,
        position: homeState.position,
        category: homeState.category,
        arrivalDay: homeState.arrivalDay,
        checkIn: homeState.checkIn,
        checkOut: homeState.checkOut,
        visible: homeState.visible,
        url: homeState.url,
        descriptionIt: homeState.descriptionIt,
        descriptionEn: homeState.descriptionEn,
        locationId: homeState.locationId,
        apartments: homeState.apartments,
        serviceJoins: homeState.serviceJoins,
        photos: homeState.photos,
        refreshHome: loadData,
        propertyInstructions: homeState.propertyInstructions,
        infoScore,
        appendPhoto,
        movePhoto,
        deletePhoto,
        updateHome,
        updateIcon,
        updateHomeServiceJoins,
        createApartment,
        refreshApartment,
        deleteApartment
    }

    return (
        <HomeContext.Provider value={context}>
            {children}
        </HomeContext.Provider>
    )
}